import React from "react";
import { Card, Table, Row, Col, Typography } from "antd";

const { Title } = Typography;

const renderSummary = (columns, data, options = {}) => {
  const percentCols = options.percentCols || [];

  const totals = {};
  columns.forEach((col) => {
    const key = col.dataIndex;
    if (!key) return;

    let sum = 0;
    let count = 0;
    let hasNumber = false;

    data.forEach((row) => {
      const raw = row && row[key];
      const num =
        typeof raw === "number"
          ? raw
          : typeof raw === "string"
            ? parseFloat(raw.toString().replace(/[, ]/g, ""))
            : NaN;

      if (!isNaN(num)) {
        hasNumber = true;
        sum += num;
        count++;
      }
    });

    if (percentCols.includes(key) && hasNumber && count > 0) {
      totals[key] = sum / 10;
    } else {
      totals[key] = hasNumber ? sum : null;
    }
  });

  return (
    <Table.Summary fixed>
      <Table.Summary.Row>
        {columns.map((col, index) => {
          if (index === 0) {
            return (
              <Table.Summary.Cell key={`summary-${index}`} index={index}>
                <strong>Grand Total</strong>
              </Table.Summary.Cell>
            );
          }

          const key = col.dataIndex;
          const total = totals[key];

          // Skip Grand Total for percentage columns
          if (percentCols.includes(key)) {
            return (
              <Table.Summary.Cell
                key={`summary-${index}`}
                index={index}
                align={col.align || "left"}
              />
            );
          }

          if (total === null || typeof total !== "number" || isNaN(total)) {
            return (
              <Table.Summary.Cell
                key={`summary-${index}`}
                index={index}
                align={col.align || "left"}
              />
            );
          }

          let formatted;
          if (percentCols.includes(key)) {
            formatted = `${total.toFixed(2)}%`;
          } else {
            formatted = total.toLocaleString("en-IN");
          }

          return (
            <Table.Summary.Cell
              key={`summary-${index}`}
              index={index}
              align={col.align || "left"}
            >
              <strong>{formatted}</strong>
            </Table.Summary.Cell>
          );
        })}
      </Table.Summary.Row>
    </Table.Summary>
  );
};

const normalizeArray = (v) => {
  if (Array.isArray(v)) return v;
  if (typeof v === "string") {
    const s = v.trim();
    if (!s) return [];
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && Array.isArray(parsed.data)) return parsed.data;
      return [];
    } catch (e) {
      try {
        const JSON5 = require("json5");
        const parsed5 = JSON5.parse(s);
        if (Array.isArray(parsed5)) return parsed5;
        if (parsed5 && Array.isArray(parsed5.data)) return parsed5.data;
      } catch (e2) { }
      return [];
    }
  }
  if (v && Array.isArray(v.data)) return v.data;
  return v ? [v] : [];
};

const prettifyGroup = (s = "") => s.replace(/_/g, " ").trim().toUpperCase();
const buildCategoryWiseColumns = (rows = []) => {
  const baseCols = [
    { title: "Sales Employee Name", dataIndex: "SalesEmployee", key: "SalesEmployee" },
  ];
  if (!rows || !rows.length) return baseCols;

  const groups = {};
  rows.forEach((r) => {
    Object.keys(r || {}).forEach((k) => {
      if (k === "SalesEmployee") return;
      const m = k.match(/^(.*)_(QTY|AMT)$/i);
      if (!m) return;
      const base = m[1];
      const type = m[2].toUpperCase();
      if (!groups[base]) groups[base] = {};
      groups[base][type] = k;
    });
  });

  const cols = [];
  Object.keys(groups).forEach((base) => {
    const pretty = prettifyGroup(base);

    if (groups[base].QTY) {
      cols.push({
        title: `${pretty} QTY`,
        dataIndex: groups[base].QTY,
        key: groups[base].QTY,
        align: "right",
      });
    }
    if (groups[base].AMT) {
      cols.push({
        title: `${pretty} Value`,
        dataIndex: groups[base].AMT,
        key: groups[base].AMT,
        align: "right",
        render: (val) => Number(val || 0).toLocaleString("en-IN"),
      });
    }
  });

  return [...baseCols, ...cols];
};

export default function DailyReviewTab({
  targetVsActual = [],
  executiveOsOd = [],
  incentiveTargetVsActual = [],
  salesOrderPending = [],
  categoryWiseSales = [],
  customerWiseSales = [],
  distributorTargetVsActual = [],
  categoryWiseSalesValue = [],
}) {
  const tableCommonProps = {
    size: "small",
    pagination: false,
    bordered: true,
    scroll: { x: "max-content" },
  };

  const titleStyle = { fontWeight: 600, marginBottom: 12 };

  const columnsMap = [
    {
      title: "Target Vs Actual Sales",
      columns: [
        { title: "Sales Employee", dataIndex: "SalesEmployee", key: "SalesEmployee" },
        {
          title: "TARGET",
          dataIndex: "Target",
          key: "Target",
          align: "right",
          render: (val) => Number(val).toLocaleString("en-IN"),
        },
        {
          title: "Value",
          dataIndex: "ActualSales",
          key: "ActualSales",
          align: "right",
          render: (val) => Number(val).toLocaleString("en-IN"),
        },
        { title: "%", dataIndex: "Percentage", key: "Percentage", align: "right" },
      ],
      data: targetVsActual,
      rowKey: "SalesEmployee",
      pagination: { pageSize: 10 },
    },
    {
      title: "Executive Wise OS/OD %",
      columns: [
        { title: "Emp", dataIndex: "EmpName", key: "EmpName" },
        {
          title: "Adv Amt",
          dataIndex: "AdvAmt",
          key: "AdvAmt",
          align: "right",
          render: (val) => Number(val).toLocaleString("en-IN"),
        },
        {
          title: "OS Amt",
          dataIndex: "OSAmt",
          key: "OSAmt",
          align: "right",
          render: (val) => Number(val).toLocaleString("en-IN"),
        },
        {
          title: "OD Amt",
          dataIndex: "ODAmt",
          key: "ODAmt",
          align: "right",
          render: (val) => Number(val).toLocaleString("en-IN"),
        },
        { title: "%", dataIndex: "Percentage", key: "Percentage", align: "right" },
      ],
      data: executiveOsOd,
      rowKey: "EmpName",
      pagination: { pageSize: 10 },
    },
    {
      title: "Incentive Target Vs Collection",
      columns: [
        { title: "Sales Employee", dataIndex: "SalesEmployee", key: "SalesEmployee" },
        {
          title: "Collection",
          dataIndex: "Collection",
          key: "Collection",
          align: "right",
          render: (val) => Number(val).toLocaleString("en-IN"),
        },
        {
          title: "W/o Tax",
          dataIndex: "IncentiveTarget",
          key: "IncentiveTarget",
          align: "right",
          render: (val) => Number(val).toLocaleString("en-IN"),
        },
        { title: "%", dataIndex: "Percentage", key: "Percentage", align: "right" },
      ],
      data: incentiveTargetVsActual,
      rowKey: "SalesEmployee",
      pagination: { pageSize: 10 },
    },
    {
      title: "Sales Order Pending",
      columns: [
        { title: "Sales Employee", dataIndex: "SalesEmployee", key: "SalesEmployee" },
        { title: "Pending Qty", dataIndex: "PendingOrders", key: "PendingOrders", align: "right" },
        {
          title: "Pending Value",
          dataIndex: "PendingValue",
          key: "PendingValue",
          align: "right",
          render: (val) => Number(val).toLocaleString("en-IN"),
        },
      ],
      data: salesOrderPending,
      rowKey: "SalesEmployee",
      pagination: { pageSize: 10 },
    },
  ];

  const cwsData = normalizeArray(categoryWiseSales);
  const catWiseColumns = buildCategoryWiseColumns(cwsData);
  const catWiseSummaryCols = catWiseColumns.map(({ title, dataIndex, align }) => ({
    title,
    dataIndex,
    align,
  }));

  return (
    <div className="sales-trend-tab" style={{ padding: 16 }}>
      <Row gutter={[24, 24]} align="stretch">
        {columnsMap.map((block, i) => (
          <Col key={i} xs={24} sm={12} lg={12} style={{ display: "flex" }}>
            <Card
              title={<Title level={5} style={{ ...titleStyle, color: "#c62828" }}>{block.title}</Title>}
              style={{
                borderRadius: 8,
                boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                width: "100%",
              }}
              bodyStyle={{
                flex: 1,
                padding: 12,
              }}
            >
              <Table
                columns={block.columns}
                dataSource={block.data}
                rowKey={block.rowKey}
                {...tableCommonProps}
                pagination={block.pagination}
                summary={() => renderSummary(block.columns, block.data, { percentCols: ["Percentage"] })}
              />
            </Card>
          </Col>
        ))}
      </Row>

      <Row style={{ marginTop: 24 }}>
        <Col span={24} style={{ display: "flex" }}>
          <Card
            title={<Title level={5} style={{ ...titleStyle, color: "#1565c0" }}>Category Wise Sales</Title>}
            style={{ borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.06)", width: "100%" }}
            bodyStyle={{ padding: 16 }}
          >
            <Table
              columns={catWiseColumns}
              dataSource={cwsData}
              rowKey={(row, idx) => (row && row.SalesEmployee) ? row.SalesEmployee : idx}
              size="small"
              bordered
              scroll={{ x: "max-content" }}
              pagination={{ pageSize: 10 }}
              summary={() => renderSummary(catWiseSummaryCols, cwsData)}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]} style={{ marginTop: 24 }} align="stretch">
        {[
          {
            title: "Customer Wise Sales Qty & Value",
            data: customerWiseSales,
            rowKey: "BillingTo",
            columns: [
              { title: "Billing_To", dataIndex: "BillingTo", key: "BillingTo" },
              { title: "Quantity", dataIndex: "Quantity", key: "Quantity", align: "right" },
              {
                title: "Row Total",
                dataIndex: "RowTotal",
                key: "RowTotal",
                align: "right",
                render: (val) => Number(val).toLocaleString("en-IN"),
              },
            ],
            pagination: { pageSize: 10 },
          },
          {
            title: "Distributor Target Vs Actual",
            data: distributorTargetVsActual,
            rowKey: "CustomerName",
            columns: [
              { title: "Customer Name", dataIndex: "CustomerName", key: "CustomerName" },
              {
                title: "Target",
                dataIndex: "Target",
                key: "Target",
                align: "right",
                render: (val) => Number(val).toLocaleString("en-IN"),
              },
              {
                title: "Value",
                dataIndex: "Value",
                key: "Value",
                align: "right",
                render: (val) => Number(val).toLocaleString("en-IN"),
              },
            ],
            pagination: { pageSize: 10 },
          },
          {
            title: "Category Wise Sales Value",
            data: categoryWiseSalesValue,
            rowKey: "ItemGroupName",
            columns: [
              { title: "Sub Group Level2", dataIndex: "ItemGroupName", key: "ItemGroupName" },
              { title: "Qty", dataIndex: "Qty", key: "Qty", align: "right" },
              {
                title: "Value",
                dataIndex: "Value",
                key: "Value",
                align: "right",
                render: (val) => Number(val).toLocaleString("en-IN"),
              },
            ],
            pagination: { pageSize: 10 },
          },
        ].map((item, index) => (
          <Col key={index} xs={24} md={12} lg={8} style={{ display: "flex", flexDirection: "column" }}>
            <Card
              title={<Title level={5} style={{ ...titleStyle, color: "#8e24aa" }}>{item.title}</Title>}
              style={{
                borderRadius: 8,
                boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                width: "100%",
                color: "#fff",
              }}
              bodyStyle={{ padding: 12, }}
            >
              <Table
                columns={item.columns}
                dataSource={item.data}
                rowKey={item.rowKey}
                size="small"
                bordered
                scroll={{ x: true }}
                pagination={item.pagination}
                summary={() => renderSummary(item.columns, item.data)}
              />
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
}
